import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import UserStatistic from './UserStatistic';
//import UserManual from './UserManual'
import CurrentDashboard from './CurrentDashboard';
import Timesheet from './Timesheet';
import MonitoringCash from './MonitoringCash';
import MonitoringKM from './MonitoringKM';
import Documents from './Documents/index';
import UserMarkingCodes from './MarkingCodes';
import GeolocationManager from './GeolocationManager';
import PharmacyMap from './PharmacyMap/index';
import Nomenclature from './Nomenclature';
import { UserProfilePage, verifyAndSyncProfileStatus, getProfileBlockingMessage } from '../profile';
import DashboardSidebar from './dashboard/DashboardSidebar';
import AppHeader from '../layout/AppHeader';
import HomePage from '../home/HomePage';
import OnlineJournal from './OnlineJournal/OnlineJournal';
import { useNotifications } from './notifications/useNotifications';
import NotificationPanel from './notifications/NotificationPanel';
import { getDisplayLogin, logoutCurrentAccount, useSessionActivity } from '../auth';
import { StaffSection } from '../staff';
import { CorporateMail } from './mail';
import { openOFDTab } from './ofdUtils';
import { PROFILE_START_PAGE, findFirstAvailablePage, findStartPage, getMissingStartPagePerms } from '../../config/startPages';
import usePermissions from '../../utils/usePermissions';
import { notify } from '../notifications';

const Dashboard: React.FC = () => {
  const userRole = localStorage.getItem('role');
  const { hasPerm } = usePermissions();
  const [selectedIndex, setSelectedIndex] = useState(99);
  const [forceProfileCheck, setForceProfileCheck] = useState(false);
  const [username, setUsername] = useState('');
  const [pharmacyNum, setPharmacyNum] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lockTimer, setLockTimer] = useState<string>('');
  const [lastLogoutTime, setLastLogoutTime] = useState<Date | null>(null);
  const navigate = useNavigate();

  const { notifications, allRead, unreadCount, markAsRead } = useNotifications(
    username,
    pharmacyNum,
    userRole
  );
  const [notificationsDismissed, setNotificationsDismissed] = useState(false);

  const fetchPharmacyNum = async (username: string) => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/user-info', {
        params: { username }
      });

      if (response.data.success) {
        const newPharmacyNum = response.data.pharmacyNum?.toString();
        setPharmacyNum(newPharmacyNum);
        localStorage.setItem('pharmacyNum', newPharmacyNum);
      } else {
        notify.warning('Не удалось получить номер аптеки');
        setPharmacyNum(null);
        localStorage.removeItem('pharmacyNum');
      }
    } catch (error) {
      console.error('Ошибка при получении номера аптеки:', error);
      notify.warning('Ошибка при получении данных');
      setPharmacyNum(null);
      localStorage.removeItem('pharmacyNum');
    } finally {
      setLoading(false);
    }
  };

  // Локальная проверка времени без запроса к серверу
  const checkLocalTimeAndLogout = () => {
    const currentTime = new Date();
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();

    if (lastLogoutTime &&
      lastLogoutTime.getHours() === hours &&
      lastLogoutTime.getDate() === currentTime.getDate()) {
      return;
    }

    // Логаут в точное время 10:00 и 22:00
    if ((hours === 10 || hours === 22) && minutes === 0 && seconds === 0) {
      handleLogout();
      setLastLogoutTime(currentTime);
    }
  };

  // Синхронизация с сервером (редко)
  const syncWithServer = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/current-time');
      setLockTimer(response.data.lockTimer || '');
    } catch (err) {
      console.error('Ошибка при синхронизации времени:', err);
    }
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedPharmacyNum = localStorage.getItem('pharmacyNum');

    if (storedUsername) {
      setUsername(storedUsername);
      if (storedPharmacyNum) {
        setPharmacyNum(storedPharmacyNum);
        setLoading(false);
      } else {
        fetchPharmacyNum(storedUsername);
      }

      // Проверка обязательного заполнения профиля
      verifyAndSyncProfileStatus(storedUsername).then((isIncomplete) => {
        if (isIncomplete) {
          setSelectedIndex(99);
        }
      });

      // Check if forced profile check is required
      const forceCheck = localStorage.getItem('forceProfileCheck');
      if (forceCheck === 'true') {
        setForceProfileCheck(true);
        setSelectedIndex(99);
      }

      // Check if profile should open immediately after login
      // Одноразовые флаги входа больше не нужны: стартовая страница открывается при каждой загрузке
      sessionStorage.removeItem('openProfileOnLogin');
      localStorage.removeItem('openProfileOnLogin');
      setSelectedIndex(99);

      // Стартовая страница роли (конструктор ролей); профиль - по умолчанию
      const startOption = findStartPage('dashboard', localStorage.getItem('startPage'));
      if (
        startOption &&
        startOption.key !== PROFILE_START_PAGE &&
        getMissingStartPagePerms(startOption, hasPerm).length === 0 &&
        forceCheck !== 'true' &&
        localStorage.getItem('profileIncomplete') !== 'true'
      ) {
        setSelectedIndex(startOption.index);
      }
    } else {
      navigate('/');
    }
    // hasPerm берётся из первого рендера (права читаются из localStorage) - повторный запуск не нужен
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Локальная проверка времени каждую секунду (БЕЗ запросов к серверу)
  useEffect(() => {
    const timeCheckInterval = setInterval(checkLocalTimeAndLogout, 1000);
    return () => clearInterval(timeCheckInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Синхронизация с сервером каждые 10 минут
  useEffect(() => {
    syncWithServer(); // Первая синхронизация при загрузке
    const serverSyncInterval = setInterval(syncWithServer, 600000); // 10 минут
    return () => clearInterval(serverSyncInterval);
  }, []);

  // Реакция на изменение статуса заполненности профиля
  useEffect(() => {
    const handleProfileStatus = () => {
      if (localStorage.getItem('profileIncomplete') === 'true') {
        setSelectedIndex(99);
      }
    };
    window.addEventListener('profileStatusChanged', handleProfileStatus);
    window.addEventListener('storage', handleProfileStatus);
    return () => {
      window.removeEventListener('profileStatusChanged', handleProfileStatus);
      window.removeEventListener('storage', handleProfileStatus);
    };
  }, []);

  // Принудительное удержание на странице профиля при незаполненных данных
  useEffect(() => {
    if (localStorage.getItem('profileIncomplete') === 'true' && selectedIndex !== 99) {
      setSelectedIndex(99);
    }
  }, [selectedIndex]);

  const handleLogout = async () => {
    if (localStorage.getItem('profileIncomplete') === 'true') {
      notify.error(getProfileBlockingMessage('logout'));
      setSelectedIndex(99);
      return;
    }
    await logoutCurrentAccount();
    // Остальные аккаунты устройства покажет экран входа; в них он сам не входит
    navigate('/', { state: { skipAutoEnter: true } });
  };

  const handleMenuItemClick = (index: number) => {
    if (localStorage.getItem('profileIncomplete') === 'true' && index !== 99) {
      notify.error(getProfileBlockingMessage('navigate'));
      setSelectedIndex(99);
      return;
    }
    if (index === 8) {
      openOFDTab();
      return;
    }
    setSelectedIndex(index);
  };

  // Внешние сервисы (база знаний) недоступны, пока не заполнен профиль
  const openExternalService = (url: string) => {
    if (localStorage.getItem('profileIncomplete') === 'true') {
      notify.error(getProfileBlockingMessage('services'));
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isInterfaceBlocked = !username || !pharmacyNum || pharmacyNum === 'NaN' || pharmacyNum === '-';
  const userGuid = localStorage.getItem('guid');
  // У сотрудника точки username - логин точки (по нему работают разделы кабинета), а шапка и почта - его собственные
  const ownLogin = getDisplayLogin();

  const getTitle = () => {
    const titles: { [key: number]: string } = {
      0: 'Текущий отчет',
      1: 'Отчет за месяц',
      3: 'Табель РБ',
      4: 'Мониторинг Кассы',
      5: 'Мониторинг КМ',
      6: 'Остатки',
      7: 'Документооборот',
      // 8: 'ОФД',
      9: 'Номенклатура',
      10: 'Электронный журнал',
      11: 'Корпоративная почта',
      12: 'КМ\'ы',
      13: 'Сотрудники',
      20: 'Главная',
      99: 'Профиль пользователя'
    };
    return titles[selectedIndex] || 'Пользовательский раздел';
  };

  // Event-driven отслеживание активности и текущего раздела пользователя
  useSessionActivity(getTitle());

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'white' }}>
        <CircularProgress sx={{ color: 'black' }} />
      </Box>
    );
  }

  // Панель уведомлений: показываем после гео-проверки, если нет принудительной проверки профиля
  // и есть хоть одно непрочитанное уведомление и пользователь ещё не закрыл панель
  const showNotificationPanel =
    !forceProfileCheck &&
    !notificationsDismissed &&
    !isInterfaceBlocked &&
    !allRead;

  const dashboardContent = (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'white', overflow: 'hidden' }}>

      <DashboardSidebar
        selectedIndex={selectedIndex}
        onMenuItemClick={handleMenuItemClick}
        onLogout={handleLogout}
        onOpenService={openExternalService}
        isInterfaceBlocked={isInterfaceBlocked}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

        <AppHeader
          title={getTitle()}
          username={ownLogin}
          onProfileClick={() => setSelectedIndex(99)}
          isProfileSelected={selectedIndex === 99}
        />

        <Box sx={{
          flexGrow: 1,
          p: 0,
          overflow: selectedIndex === 11 ? 'hidden' : 'auto',
          bgcolor: '#ffffff', // Pure white background for content
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}>
          {isInterfaceBlocked ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
              <Typography variant="h6" color="error">
                Доступ ограничен. Пожалуйста, обратитесь к МАСу или в IT отдел.
              </Typography>
            </Box>
          ) : selectedIndex === 11 ? (
            <CorporateMail
              username={ownLogin}
              onNavigateToProfile={() => {
                setSelectedIndex(99);
                setTimeout(() => {
                  document.getElementById('section-mail')?.scrollIntoView({ behavior: 'smooth' });
                }, 150);
              }}
            />
          ) : (
            <Box sx={{ p: 4, minHeight: '100%' }}>
              {selectedIndex === 20 && hasPerm('home.menu') && <HomePage targetInterface="dashboard" />}
              {selectedIndex === 0 && hasPerm('reports.menu') && hasPerm('reports.current.view') && <CurrentDashboard />}
              {selectedIndex === 1 && hasPerm('reports.menu') && hasPerm('reports.monthly.view') && <UserStatistic />}
              {selectedIndex === 7 && hasPerm('documents.menu') && hasPerm('documents.flow.view') && <Documents />}
              {selectedIndex === 3 && hasPerm('timesheet.menu') && <Timesheet pharmacyNum={pharmacyNum} />}
              {selectedIndex === 4 && hasPerm('monitoring.menu') && hasPerm('monitoring.cash.view') && <MonitoringCash />}
              {selectedIndex === 5 && hasPerm('monitoring.menu') && hasPerm('monitoring.km.view') && <MonitoringKM />}
              {selectedIndex === 6 && hasPerm('guide.remains.menu') && <PharmacyMap />}
              {selectedIndex === 9 && hasPerm('guide.nomenclature.menu') && <Nomenclature />}
              {selectedIndex === 10 && hasPerm('journal.menu') && <OnlineJournal />}
              {selectedIndex === 12 && hasPerm('documents.menu') && hasPerm('documents.marking.view') && <UserMarkingCodes />}
              {selectedIndex === 13 && hasPerm('staff.menu') && <StaffSection mode="point" />}
              {selectedIndex === 99 && (
                <UserProfilePage
                  username={username}
                  pharmacyNum={pharmacyNum}
                  userRole={userRole}
                  forceCheck={forceProfileCheck}
                  onConfirm={() => {
                    localStorage.removeItem('forceProfileCheck');
                    setForceProfileCheck(false);
                    if (localStorage.getItem('profileIncomplete') !== 'true') {
                      // После подтверждения профиля - стартовая страница роли, иначе первый доступный пункт меню
                      const startOption = findStartPage('dashboard', localStorage.getItem('startPage'));
                      const target = startOption && startOption.key !== PROFILE_START_PAGE && getMissingStartPagePerms(startOption, hasPerm).length === 0
                        ? startOption
                        : findFirstAvailablePage('dashboard', hasPerm);
                      setSelectedIndex(target ? target.index : 99);
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Панель уведомлений - рендерится поверх всего через position:fixed внутри компонента */}
      {showNotificationPanel && (
        <NotificationPanel
          notifications={notifications}
          allRead={allRead}
          unreadCount={unreadCount}
          onMarkAsRead={markAsRead}
          onClose={() => setNotificationsDismissed(true)}
        />
      )}

    </Box>
  );

  // Для складов (storage) не требуется подтверждение геолокации
  if (userRole === 'storage') {
    return dashboardContent;
  }

  return (
    <GeolocationManager username={username} guid={userGuid}>
      {dashboardContent}
    </GeolocationManager>
  );
};

export default Dashboard;
